import { useEffect, useState } from "react";

import SelectMenu from "../controls/SelectMenu";
import Button from "../ui/Button";
import { readDefaultLocationFromStorage } from "../../shared/defaultLocationStorage";
import {
  fetchLocationsForDropdown,
  fetchLocationZonesForDropdown,
} from "../../shared/locationDropdowns";

function getTransferCountLabel(count, items) {
  if (items.every((item) => item.type === "pallet")) {
    return `${count} pallet${count === 1 ? "" : "s"}`;
  }
  if (items.every((item) => item.type === "carton")) {
    return `${count} carton${count === 1 ? "" : "s"}`;
  }
  return `${count} item${count === 1 ? "" : "s"}`;
}

function getTransferSerialLabel(item) {
  return item?.serial_number || "—";
}

export default function PackagingTransferModal(props) {
  const { selectedItems = [], onConfirm, onCancel, loading = false } = props;

  const [locationOptions, setLocationOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [location, setLocation] = useState(null);
  const [zone, setZone] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadLocations() {
      setOptionsLoading(true);
      const result = await fetchLocationsForDropdown();
      if (cancelled) {
        return;
      }

      if (result.success) {
        setLocationOptions(result.data ?? []);
        const defaultLocation = readDefaultLocationFromStorage();
        if (defaultLocation) {
          setLocation(defaultLocation);
        }
      }
      setOptionsLoading(false);
    }

    loadLocations();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!location?.value) {
      setZoneOptions([]);
      setZone(null);
      return;
    }

    let cancelled = false;

    async function loadZones() {
      const result = await fetchLocationZonesForDropdown(location);
      if (cancelled) {
        return;
      }
      setZoneOptions(result.success ? (result.data ?? []) : []);
    }

    loadZones();

    return () => {
      cancelled = true;
    };
  }, [location?.value]);

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    setZone(null);
  };

  const handleTransfer = async () => {
    if (!location?.value || !zone?.value) {
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm?.({ location, zone });
    } finally {
      setSubmitting(false);
    }
  };

  const canTransfer =
    Boolean(location?.value) && Boolean(zone?.value) && !optionsLoading;

  const itemCount = selectedItems.length;
  const showSerialList = itemCount <= 5;
  const busy = loading || submitting;

  return (
    <div className="p-6 sm:p-8">
      <h3 className="text-lg font-semibold text-[#0F172A]">
        Transfer packaging
      </h3>
      <p className="mt-1 text-sm text-[#64748B]">
        Move {selectedItems.length} selected item
        {selectedItems.length === 1 ? "" : "s"} to a new location.
      </p>

      <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 text-sm text-[#475569]">
        {showSerialList ? (
          <ul className="max-h-32 space-y-1 overflow-y-auto">
            {selectedItems.map((item) => (
              <li
                key={item.barcode ?? `${item.id}-${item.serial_number}`}
                className="truncate"
              >
                {getTransferSerialLabel(item)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="font-medium text-[#0F172A]">
            Transferring {getTransferCountLabel(itemCount, selectedItems)}
          </p>
        )}
      </div>

      <div className="mt-5 space-y-4">
        <SelectMenu
          handleInputChange={handleLocationChange}
          name="transfer_location"
          options={locationOptions}
          value={location}
          title="Location"
          placeholder={
            optionsLoading ? "Loading locations…" : "Select location…"
          }
          disabled={optionsLoading || busy}
        />
        <SelectMenu
          handleInputChange={(e) => setZone(e.target.value)}
          name="transfer_zone"
          options={zoneOptions}
          value={zone}
          title="Zone *"
          placeholder={
            location?.value ? "Select zone…" : "Select location first…"
          }
          disabled={!location?.value || busy || zoneOptions.length === 0}
        />
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={handleTransfer}
          loading={busy}
          disabled={!canTransfer}
        >
          Transfer
        </Button>
      </div>
    </div>
  );
}
